import { useEffect, useRef } from "react";
import { useProgressContext } from "../context/ProgressContext";

/**
 * Upload/encode state for components.
 *
 * This used to *be* the state machine: it owned the encode poller, capped it at ~12
 * minutes and then removed the item as if it had succeeded. All of that now lives in
 * src/upload/uploadManager.js, where it survives unmounts and page navigation. This
 * hook is just the component-facing seam.
 *
 * @param {Function} [fetchMedia] Called when an upload appears/finishes so the media
 *   grid can refresh. Registered with the manager, which owns the timing.
 * @param {string}   [activeTab]  Tab to refresh.
 */
const useProgress = (fetchMedia, activeTab) => {
  const ctx = useProgressContext();
  const { setGridRefresh } = ctx;

  // Callers recreate fetchMedia on every render, so keep it behind a ref and register
  // a single stable callback with the manager instead of re-registering constantly.
  const latest = useRef({ fetchMedia, activeTab });
  latest.current = { fetchMedia, activeTab };

  useEffect(() => {
    if (typeof latest.current.fetchMedia !== "function") return undefined;
    setGridRefresh(() => {
      const { fetchMedia: fn, activeTab: tab } = latest.current;
      if (typeof fn === "function") fn(tab);
    });
    return () => setGridRefresh(null);
  }, [setGridRefresh]);

  return ctx;
};

export default useProgress;
